import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, CreditCard, LineChart, Bell, Users, CheckCircle, Play, Timer, Medal, ShieldCheck, ArrowUp, ChevronRight, Menu, X } from "lucide-react";
import Logo from "@/components/ui/logo";
import swimmingPoolBlue from '@/assets/swimming-pool-blue.jpg'; // Placeholder for fallback color

interface LandingPageProps {
  className?: string;
}

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Testimonial {
  text: string;
  author: string;
  avatar: string;
}

interface Stat {
  value: string;
  label: string;
}

interface Partner {
  title: string;
  description: string;
  icon: string;
}

interface NewsItem {
  title: string;
  excerpt: string;
  image: string;
}

export const LandingPage: React.FC<LandingPageProps> = ({ className = '' }) => {
  const { isSignedIn } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 300);
    };

    // Prevent body scroll when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMobileMenu();
  };

  const handleMenuOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(false);
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsMobileMenuOpen(false);
    }
  };

  const features: Feature[] = [
    {
      icon: <Users className="h-12 w-12 text-primary" />,
      title: "Attendance Tracking",
      description: "Create events, manage RSVPs, and track attendance in real-time."
    },
    {
      icon: <CreditCard className="h-12 w-12 text-primary" />,
      title: "Payment Management",
      description: "Seamlessly collect fees and dues with integrated payment processing."
    },
    {
      icon: <Bell className="h-12 w-12 text-primary" />,
      title: "Reminders & Communication",
      description: "Automated alerts for sessions, deadlines, and team announcements."
    },
    {
      icon: <Calendar className="h-12 w-12 text-primary" />,
      title: "Scheduling",
      description: "Unified team calendar with sync to Google and Apple Calendar."
    },
    {
      icon: <LineChart className="h-12 w-12 text-primary" />,
      title: "Performance Tracking",
      description: "Track records, analyze improvement, and set performance goals."
    },
    {
      icon: <CheckCircle className="h-12 w-12 text-primary" />,
      title: "Role-Based Access",
      description: "Customized experiences for coaches, swimmers, and parents."
    }
  ];

  const testimonials: Testimonial[] = [
    {
      text: "I love what you've done with the platform. My kids absolutely love attending. It's a refreshing and a great program. Always making them feel safe and wanting him enjoy the water in the future.",
      author: "Enzo Wade",
      avatar: "/lovable-uploads/45ab384a-987f-4d03-9357-87f78703a3c2.png"
    },
    {
      text: "I'm very impressed with Krystal's services. I see how my kids grew and how they've improved their swimming skills through targeted coaching and amazing digital tools.",
      author: "Karl West",
      avatar: "/lovable-uploads/45ab384a-987f-4d03-9357-87f78703a3c2.png"
    },
    {
      text: "The most innovative swimming solution I've seen! My kids love all of the digital features. The schedule syncs to our calendar and the swimming performance tracking has been incredible for their progress.",
      author: "Andrea Reyes",
      avatar: "/lovable-uploads/45ab384a-987f-4d03-9357-87f78703a3c2.png"
    }
  ];
  
  const stats: Stat[] = [
    { value: "500+", label: "Swimmers Enrolled" },
    { value: "250+", label: "Happy Swimmers" },
    { value: "27+", label: "Local Partners" },
    { value: "35+", label: "Super Agents" }
  ];

  const partners: Partner[] = [
    {
      title: "Coaches",
      description: "Are you the top swimming coach around? We're searching a qualified staff to join our team",
      icon: "👨‍🏫"
    },
    {
      title: "School Administrators",
      description: "Do you run a top swimming academy with high standards? If you would like to partner with us to provide quality swimming education",
      icon: "🏛️"
    },
    {
      title: "Parents",
      description: "We will show you how you as a parent can support your child's progress for a swim. This is a unique opportunity to help your child feel comfortable in the swimming course",
      icon: "👪"
    },
    {
      title: "Social Media Partners",
      description: "If you are interested in promoting our mission to make swimming skills accessible to every child in the world",
      icon: "📱"
    }
  ];

  return (
    <div className={`min-h-screen ${className}`}>
      {/* Hero Section with Background */}
      <div className="relative min-h-screen">
        <div className="absolute inset-0">
          <img
            src="/images/hero-swimmers.jpg"
            alt="Swimming athletes"
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/images/pool_teen.jpg'; // Fallback image
            }}
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Navigation */}
        <nav className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-blue-900/80 backdrop-blur-md' : 'bg-transparent'
        }`}>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center">
                <Logo 
                  variant="light"
                  showText={false} 
                  className="transition-all duration-300"
                />
              </div>
              
              {/* Desktop Navigation */}
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  <a href="#" className="nav-link">
                    Home
                  </a>
                  <a href="#features" className="nav-link">
                    Features
                  </a>
                  <a href="#pricing" className="nav-link">
                    Pricing
                  </a>
                  <a href="#contact" className="nav-link">
                    Contact
                  </a>
                </div>
              </div>
              
              {/* Desktop Auth Buttons and Mobile Menu */}
              <div className="flex items-center">
                {/* Desktop Auth Buttons */}
                <div className="hidden md:flex items-center space-x-4">
                  {isSignedIn ? (
                    <Link to="/dashboard" className="nav-glass-button">
                      Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link to="/sign-in" className="nav-glass-button">
                        Sign In
                      </Link>
                      <Link to="/sign-up" className="nav-glass-button primary">
                        Try for Free
                      </Link>
                    </>
                  )}
                </div>
                {/* Mobile Menu Button */}
                <button
                  type="button"
                  className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-white hover:text-blue-200 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={toggleMobileMenu}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-menu"
                >
                  <span className="sr-only">Open main menu</span>
                  {isMobileMenuOpen ? (
                    <X className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Menu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Navigation Menu */}
            <div 
              id="mobile-menu"
              className={`
                md:hidden 
                fixed inset-0 top-20 
                bg-blue-900/95 backdrop-blur-md
                transition-all duration-300 ease-in-out
                ${isMobileMenuOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible'}
                p-6 space-y-6
                shadow-lg
                h-[calc(100vh-5rem)]
                overflow-y-auto
                z-50
              `}
              onClick={handleMenuOverlayClick}
            >
              <div 
                className="flex flex-col space-y-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="space-y-4">
                  <button 
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setIsMobileMenuOpen(false);
                    }}
                    className="mobile-nav-link block w-full text-left py-4 px-6 text-lg font-medium hover:bg-white/10 rounded-lg transition-colors duration-200" 
                  >
                    Home
                  </button>
                  <button 
                    onClick={() => {
                      const featuresSection = document.getElementById('features');
                      featuresSection?.scrollIntoView({ behavior: 'smooth' });
                      setIsMobileMenuOpen(false);
                    }}
                    className="mobile-nav-link block w-full text-left py-4 px-6 text-lg font-medium hover:bg-white/10 rounded-lg transition-colors duration-200" 
                  >
                    Features
                  </button>
                  <button 
                    onClick={() => {
                      const pricingSection = document.getElementById('pricing');
                      pricingSection?.scrollIntoView({ behavior: 'smooth' });
                      setIsMobileMenuOpen(false);
                    }}
                    className="mobile-nav-link block w-full text-left py-4 px-6 text-lg font-medium hover:bg-white/10 rounded-lg transition-colors duration-200" 
                  >
                    Pricing
                  </button>
                  <button 
                    onClick={() => {
                      const contactSection = document.getElementById('contact');
                      contactSection?.scrollIntoView({ behavior: 'smooth' });
                      setIsMobileMenuOpen(false);
                    }}
                    className="mobile-nav-link block w-full text-left py-4 px-6 text-lg font-medium hover:bg-white/10 rounded-lg transition-colors duration-200" 
                  >
                    Contact
                  </button>
                </div>
                <div className="pt-6 border-t border-blue-700">
                  {isSignedIn ? (
                    <Link 
                      to="/dashboard" 
                      className="block w-full text-center nav-glass-button py-4 text-lg font-medium hover:bg-white/20 transition-colors duration-200"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <div className="flex flex-col space-y-4">
                      <Link 
                        to="/sign-in" 
                        className="block w-full text-center nav-glass-button py-4 text-lg font-medium hover:bg-white/20 transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Sign In
                      </Link>
                      <Link 
                        to="/sign-up" 
                        className="block w-full text-center nav-glass-button primary py-4 text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Try for Free
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="hero-title text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                Manage Your Sports Team <br />
                Like a <span className="text-[#F3A712]">Champion</span>
              </h1>
              <p className="hero-subtitle mt-8 text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto">
                Streamline scheduling, payments, and performance tracking in one powerful platform
              </p>
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-x-6">
                {isSignedIn ? (
                  <Link to="/dashboard" className="w-full sm:w-auto glass-button">
                    Continue to Dashboard <ChevronRight className="inline-block ml-2 w-5 h-5" />
                  </Link>
                ) : (
                  <>
                    <Link to="/sign-up" className="w-full sm:w-auto glass-button">
                      Get Started For Free <ChevronRight className="inline-block ml-2 w-5 h-5" />
                    </Link>
                    <Link to="/learn-more" className="w-full sm:w-auto glass-button-secondary">
                      Learn More
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 md:text-4xl">
              Everything You Need to Succeed
            </h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600">
              Powerful features designed for modern sports management
            </p>
          </div>
          <div className="mt-12 sm:mt-16 grid grid-cols-1 gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="relative p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-blue-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Section with Background */}
      <section id="contact" className="relative py-24">
        <div className="absolute inset-0">
          <img
            src="/images/pool_teen.jpg"
            alt="Swimming pool"
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.backgroundColor = '#1E3A8A'; // Fallback color
            }}
          />
          <div className="absolute inset-0 bg-blue-900/70" />
        </div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Become a Partner
            </h2>
            <p className="mt-4 text-lg text-gray-100">
              Join our network of successful sports organizations
            </p>
            <div className="mt-8">
              <Link to="/partner" className="glass-button">
                Partner With Us <ChevronRight className="inline-block ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Choose the perfect plan for your team's needs
            </p>
          </div>
          <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Basic Plan */}
            <div className="flex flex-col p-6 bg-white rounded-lg border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-gray-900">Starter</h3>
              <p className="mt-4 text-gray-600">Perfect for small teams</p>
              <div className="mt-8">
                <span className="text-4xl font-bold">$0</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Up to 10 team members</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Team scheduling</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Basic team management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Payment processing</span>
                </li>
              </ul>
              <Link to="/sign-up" className="mt-8 glass-button w-full text-center">
                Get Started
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="flex flex-col p-6 bg-blue-50 rounded-lg border border-blue-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-blue-900">Ligi Ndogo</h3>
              <p className="mt-4 text-blue-600">Most popular for growing teams</p>
              <div className="mt-8">
                <span className="text-4xl font-bold">$59</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Up to 30 team members</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">All Free features, plus:</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Performance tracking</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Priority support</span>
                </li>
              </ul>
              <Link to="/sign-up" className="mt-8 glass-button w-full text-center">
                Get Started
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="flex flex-col p-6 bg-white rounded-lg border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-gray-900">Team Mkuu</h3>
              <p className="mt-4 text-gray-600">For large organizations</p>
              <div className="mt-8">
                <span className="text-4xl font-bold">$150</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Unlimited team members</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Custom features</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Dedicated support</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="ml-2">Custom integrations</span>
                </li>
              </ul>
              <Link to="/contact" className="mt-8 glass-button w-full text-center">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-nav">
            <a href="#">Home</a>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#contact">Contact</a>
            <Link to="/support">Support</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Krystal powered by Fourbic</p>
            {/* <p className="mt-1">Fourbic Ltd registered in Kenya | Company number - 06361033</p> */}
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-2 bg-[#F3A712] text-white rounded-full shadow-lg hover:bg-[#F3A712]/90 transition-colors duration-200 z-50"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default LandingPage;